import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Save, Send, Plus, Trash2, CalendarCheck } from "lucide-react";

const SLOT_TIMES = { mattino: ["09:30", "12:30"], pomeriggio: ["15:00", "18:00"] };
const newKey = () => Math.random().toString(36).slice(2, 10);

export const ProgramDialog = ({ booking, calendar, onClose, onUpdated }) => {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!booking) return;
    if (booking.program) return setP({ ...booking.program, lab_slots: (booking.program.lab_slots || []).map((s) => ({ ...s, _key: s._key || newKey() })) });
    setP({
      lab_slots: (booking.selected_labs || []).map((l) => ({ _key: newKey(), lab_title: l.lab_title, date: l.date, slot: "mattino", start: "09:30", end: "12:30", participants: l.participants, notes: "" })),
      meals: { breakfast: "08:00", lunch: "13:00", dinner: "19:30" },
      arrival_time: "12:00", departure_time: "10:00", notes: "",
    });
  }, [booking]);

  if (!booking || !p) return null;

  const setSlot = (i, field, value) => {
    const lab_slots = p.lab_slots.map((s, ix) => {
      if (ix !== i) return s;
      const n = { ...s, [field]: value };
      if (field === "slot") [n.start, n.end] = SLOT_TIMES[value];
      return n;
    });
    setP({ ...p, lab_slots });
  };
  const addSlot = () => setP({ ...p, lab_slots: [...p.lab_slots, { _key: newKey(), lab_title: "", date: booking.check_in, slot: "pomeriggio", start: "15:00", end: "18:00", participants: booking.num_participants, notes: "" }] });
  const removeSlot = (i) => setP({ ...p, lab_slots: p.lab_slots.filter((_, ix) => ix !== i) });

  const payload = () => ({ ...p, lab_slots: p.lab_slots.map(({ _key, ...s }) => ({ ...s, participants: Number(s.participants) || 0 })) });

  const save = async (confirm = false) => {
    if (p.lab_slots.some((s) => !s.lab_title || !s.date)) return toast.error("Ogni laboratorio deve avere nome e data");
    setBusy(true);
    try {
      let { data } = await api.put(`/bookings/${booking.id}/program`, payload());
      if (confirm) {
        ({ data } = await api.post(`/bookings/${booking.id}/program/confirm`));
        toast.success(`Programma confermato e inviato a ${booking.contact_email}${calendar?.connected ? " · Calendario aggiornato" : ""}`);
      } else toast.success("Programma salvato");
      onUpdated(data);
      if (confirm) onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };

  const canConfirm = booking.status === "confirmed";

  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="program-dialog">
        <DialogHeader><DialogTitle className="font-display text-3xl">Programma · {booking.reference}</DialogTitle></DialogHeader>
        <div className="text-sm text-[#6B6960] -mt-2">
          {booking.organization || booking.group_type} · {booking.num_participants} pax · Arrivo {booking.check_in} → Partenza {booking.check_out}
          {!canConfirm && <div className="text-[#8C3A27] mt-1">La pratica deve essere Confermata per inviare il programma e creare gli eventi in calendario.</div>}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
          <div><Label>Arrivo dalle</Label><Input type="time" value={p.arrival_time} onChange={(e) => setP({ ...p, arrival_time: e.target.value })} data-testid="program-arrival" /></div>
          <div><Label>Partenza entro</Label><Input type="time" value={p.departure_time} onChange={(e) => setP({ ...p, departure_time: e.target.value })} data-testid="program-departure" /></div>
          <div><Label>Colazione</Label><Input type="time" value={p.meals.breakfast} onChange={(e) => setP({ ...p, meals: { ...p.meals, breakfast: e.target.value } })} data-testid="program-breakfast" /></div>
          <div><Label>Pranzo</Label><Input type="time" value={p.meals.lunch} onChange={(e) => setP({ ...p, meals: { ...p.meals, lunch: e.target.value } })} data-testid="program-lunch" /></div>
          <div><Label>Cena</Label><Input type="time" value={p.meals.dinner} onChange={(e) => setP({ ...p, meals: { ...p.meals, dinner: e.target.value } })} data-testid="program-dinner" /></div>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between">
            <div className="overline">Laboratori · Mattino / Pomeriggio</div>
            <Button size="sm" variant="outline" onClick={addSlot} className="rounded-full" data-testid="program-add-slot"><Plus size={14} className="mr-1" /> Aggiungi</Button>
          </div>
          {p.lab_slots.length === 0 && <div className="text-sm text-[#6B6960] mt-2">Nessun laboratorio in programma.</div>}
          {p.lab_slots.map((s, i) => (
            <div key={s._key} className="grid grid-cols-12 gap-2 items-end mt-3 border-b border-[#E2DFD6] pb-3" data-testid={`program-slot-${i}`}>
              <div className="col-span-12 md:col-span-3"><Label>Laboratorio</Label><Input value={s.lab_title} onChange={(e) => setSlot(i, "lab_title", e.target.value)} /></div>
              <div className="col-span-6 md:col-span-2"><Label>Data</Label><Input type="date" min={booking.check_in} max={booking.check_out} value={s.date} onChange={(e) => setSlot(i, "date", e.target.value)} /></div>
              <div className="col-span-6 md:col-span-2"><Label>Slot</Label>
                <Select value={s.slot} onValueChange={(v) => setSlot(i, "slot", v)}>
                  <SelectTrigger data-testid={`program-slot-select-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="mattino">Mattino</SelectItem><SelectItem value="pomeriggio">Pomeriggio</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="col-span-4 md:col-span-2"><Label>Dalle</Label><Input type="time" value={s.start} onChange={(e) => setSlot(i, "start", e.target.value)} /></div>
              <div className="col-span-4 md:col-span-1"><Label>Alle</Label><Input type="time" value={s.end} onChange={(e) => setSlot(i, "end", e.target.value)} /></div>
              <div className="col-span-3 md:col-span-1"><Label>Pax</Label><Input type="number" min="1" value={s.participants} onChange={(e) => setSlot(i, "participants", e.target.value)} /></div>
              <button type="button" onClick={() => removeSlot(i)} className="col-span-1 h-10 flex items-center justify-center text-[#8C3A27]"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>

        <div className="mt-6"><Label>Note per il referente (opzionale)</Label><Textarea rows={3} value={p.notes || ""} onChange={(e) => setP({ ...p, notes: e.target.value })} data-testid="program-notes" /></div>

        <div className="mt-6 flex items-center justify-between border-t border-[#E2DFD6] pt-4 flex-wrap gap-3">
          <div className="text-xs text-[#6B6960]">
            {p.sent_at ? `Ultimo invio programma: ${new Date(p.sent_at).toLocaleString("it-IT")}` : "Programma non ancora inviato"}
            {booking.calendar_synced_at && <><br/><CalendarCheck size={12} className="inline mr-1" />Calendario sincronizzato: {new Date(booking.calendar_synced_at).toLocaleString("it-IT")}</>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => save(false)} disabled={busy} className="rounded-full" data-testid="program-save"><Save size={16} className="mr-2" /> Salva</Button>
            <Button onClick={() => save(true)} disabled={busy || !canConfirm} className="btn-primary-cdm rounded-full" data-testid="program-confirm"><Send size={16} className="mr-2" /> {busy ? "Invio…" : "Conferma programma e invia"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
